import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    InputAdornment,
    useTheme,
    TableSortLabel,
    TablePagination,
    Chip,
    Grid,
    Tooltip,
    Menu,
    MenuItem,
    Checkbox,
    ListItemText,
    Avatar,
    Stack,
    styled, // NEW: For creating custom styled components
} from "@mui/material";
import {
    MdRefresh,
    MdSearch,
    MdPersonOff,
    MdSwapHorizontalCircle, // More modern icon
    MdDownload,
    MdOutlineSchool,
    MdSchool,
    MdFormatListBulleted, // For Column Toggle
    MdSort
} from "react-icons/md";
import ChangeCourseDialog from "./ChangeCourseDialog.jsx";
import { fetchUsers } from "../../api/adminApi.jsx";

// =================================================================
// 🎨 1. CUSTOM STYLED COMPONENTS FOR AESTHETICS (Glassmorphism & Scrollbar)
// =================================================================

// Custom Scrollbar Style (Applies to the TableContainer)
const CustomScrollbarTableContainer = styled(TableContainer)(({ theme }) => ({
    maxHeight: 600,
    // Custom Scrollbar styling (Webkit for Chrome/Safari)
    '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.primary.light,
        borderRadius: '10px',
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: theme.palette.grey[200],
    },
}));

// Glassmorphic Paper Style
const GlassmorphicPaper = styled(Paper)(({ theme }) => ({
    borderRadius: 16,
    // Base Glassmorphism effect
    backgroundColor: 'rgba(255, 255, 255, 0.7)', // Semi-transparent white
    boxShadow: theme.shadows[10],
    backdropFilter: 'blur(10px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    overflow: 'hidden',
}));

// Styled Table Row for Professionalism and visual cues
const StyledTableRow = styled(TableRow)(({ theme, isUnenrolled }) => ({
    '&:nth-of-type(even)': {
        backgroundColor: 'rgba(255, 255, 255, 0.8)', // Lighter glass on even
    },
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
    ...(isUnenrolled && {
        // Subtle alert for unenrolled status
        backgroundColor: theme.palette.error.light + '1A',
        borderLeft: `4px solid ${theme.palette.error.main}`,
        '&:hover': {
            backgroundColor: theme.palette.error.light + '33',
        },
    }),
}));

// =================================================================
// ⚙️ 2. UTILITY FUNCTIONS (Kept and slightly optimized)
// =================================================================

// Type Definition (moved inside for better encapsulation)
/**
 * @typedef {object} User
 * @property {string | number} id
 * @property {string} name
 * @property {string | null} course_name
 * @property {string} email
 */

// ... (descendingComparator, getComparator, stableSort remain the same) ...
function descendingComparator(a, b, orderBy) {
    const valA = a[orderBy] === null || a[orderBy] === undefined ? '' : a[orderBy];
    const valB = b[orderBy] === null || b[orderBy] === undefined ? '' : b[orderBy];
    if (valB < valA) return -1;
    if (valB > valA) return 1;
    return 0;
}
function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}
// Color and Initial Utilities
const stringToColor = (str) => {
    let hash = 0;
    let i;
    for (i = 0; i < str.length; i += 1) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
};

const getInitials = (name) => {
    if (!name) return "??";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
};

// Define Head Cells
const initialHeadCells = [
    { id: 'name', label: 'Student Name', sortable: true, defaultVisible: true },
    { id: 'email', label: 'ID / Email', sortable: true, defaultVisible: true },
    { id: 'course_name', label: 'Current Course', sortable: true, defaultVisible: true },
    { id: 'actions', label: 'Actions', sortable: false, align: 'right', defaultVisible: true },
];

// =================================================================
// ⚛️ 3. MAIN COMPONENT
// =================================================================
const CourseControl = () => {
    const theme = useTheme();

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [updateSuccess, setUpdateSuccess] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const [selectedUser, setSelectedUser] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    // Table State
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [visibleColumns, setVisibleColumns] = useState(initialHeadCells.map(c => c.id));
    const [anchorEl, setAnchorEl] = useState(null);

    // --- Handlers ---
    const handleRequestSort = useCallback((property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    }, [order, orderBy]);

    const handleChangePage = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    const handleColumnMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleColumnMenuClose = () => {
        setAnchorEl(null);
    };

    const handleToggleColumn = (columnId) => {
        setVisibleColumns(prev =>
            prev.includes(columnId)
                ? prev.filter(id => id !== columnId)
                : [...prev, columnId].sort((a, b) => initialHeadCells.findIndex(c => c.id === a) - initialHeadCells.findIndex(c => c.id === b))
        );
    };

    // Data Fetching
    const loadUsers = useCallback(async () => {
        setLoading(true);
        setFetchError(null);
        setPage(0);
        try {
            // Mocking a slight delay for better UX with the 'Refreshing' state
            // const res = await Promise.race([fetchUsers(), new Promise(resolve => setTimeout(resolve, 500))]);
            const res = await fetchUsers();
            setUsers(res.data || []);
        } catch (error) {
            console.error("Fetch Users Error:", error);
            setFetchError("Failed to load student list. Please check the network connection or API.");
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    // Search, Filter, and Sort Logic (Memoized)
    const filteredAndSortedUsers = useMemo(() => {
        const lowerCaseSearch = searchTerm.toLowerCase();

        const filtered = users.filter(user =>
            user.name.toLowerCase().includes(lowerCaseSearch) ||
            user.email.toLowerCase().includes(lowerCaseSearch) ||
            user.id.toString().includes(lowerCaseSearch) ||
            (user.course_name && user.course_name.toLowerCase().includes(lowerCaseSearch))
        );

        return stableSort(filtered, getComparator(order, orderBy));
    }, [users, searchTerm, order, orderBy]);

    const paginatedUsers = useMemo(() => {
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        return filteredAndSortedUsers.slice(start, end);
    }, [filteredAndSortedUsers, page, rowsPerPage]);

    // CSV Export Functionality
    const handleExportCSV = useCallback(() => {
        if (filteredAndSortedUsers.length === 0) return;

        const headers = initialHeadCells.filter(c => c.id !== 'actions').map(c => c.label);

        const data = filteredAndSortedUsers.map(user =>
            initialHeadCells
                .filter(c => c.id !== 'actions')
                .map(c => {
                    const value = user[c.id];
                    // Handle commas in data by wrapping with quotes (basic CSV escaping)
                    let cellValue = (c.id === 'course_name' && value === null) ? 'Unenrolled' : String(value || '');
                    if (cellValue.includes(',')) {
                        cellValue = `"${cellValue.replace(/"/g, '""')}"`; // Double quotes inside
                    }
                    return cellValue;
                })
                .join(',')
        );

        const csvContent = [headers.join(','), ...data].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'student_enrollment_data.csv');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }, [filteredAndSortedUsers]);

    // Dialog/Snackbar Handlers
    const handleUpdateSuccess = useCallback((/** @type {string | null} */ newCourseName) => {
        setUsers(prevUsers =>
            prevUsers.map(u =>
                u.id === selectedUser.id
                    ? { ...u, course_name: newCourseName || null }
                    : u
            )
        );
        setDialogOpen(false);
        setUpdateSuccess(true);
    }, [selectedUser]);

    const handleOpenDialog = useCallback((/** @type {User} */ user) => {
        setSelectedUser(user);
        setDialogOpen(true);
    }, []);

    const handleCloseSnackbar = useCallback((event, reason) => {
        if (reason === 'clickaway') return;
        setUpdateSuccess(false);
    }, []);

    // Only show the header cells that are currently visible
    const displayedHeadCells = useMemo(() => {
        return initialHeadCells.filter(cell => visibleColumns.includes(cell.id));
    }, [visibleColumns]);

    return (
        <Box
            p={4}
            sx={{
                backgroundColor: theme.palette.grey[50], // Light background for contrast
                minHeight: '100vh',
                backgroundImage: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' // Subtle gradient
            }}
        >
            {/* --- TOP SECTION: Title and Control Grid --- */}
            <Typography
                variant="h3"
                component="h1"
                fontWeight={800} // Bolder font weight
                color={theme.palette.primary.dark}
                mb={1}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
                <MdOutlineSchool size={40} /> Student Enrollment Management
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" mb={4}>
                Easily search, sort, and reassign student courses within the system.
            </Typography>

            <Grid container spacing={2} alignItems="center" mb={4}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Search Students (Name, ID, Course)"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(0);
                        }}
                        InputProps={{
                            startAdornment: (<InputAdornment position="start"><MdSearch color={theme.palette.primary.main} /></InputAdornment>),
                            sx: { borderRadius: theme.shape.borderRadius * 2, bgcolor: 'rgba(255, 255, 255, 0.9)' } // White, rounded input
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} alignItems="center" gap={1}>
                        <Tooltip title="Toggle Visible Columns">
                            <Button
                                variant="outlined"
                                onClick={handleColumnMenuOpen}
                                startIcon={<MdFormatListBulleted />}
                                sx={{ borderRadius: 10, borderColor: theme.palette.primary.light, color: theme.palette.primary.dark }}
                            >
                                Columns
                            </Button>
                        </Tooltip>
                        <Tooltip title="Export current filtered data to CSV">
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleExportCSV}
                                startIcon={<MdDownload />}
                                disabled={filteredAndSortedUsers.length === 0}
                                sx={{ borderRadius: 10, bgcolor: theme.palette.success.main, '&:hover': { bgcolor: theme.palette.success.dark } }}
                            >
                                Export
                            </Button>
                        </Tooltip>
                        <Tooltip title="Reload all data from the API">
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={loadUsers}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <MdRefresh />}
                                sx={{ borderRadius: 10 }}
                            >
                                {loading ? "Refreshing..." : "Refresh Data"}
                            </Button>
                        </Tooltip>
                    </Box>
                </Grid>
            </Grid>

            {/* Column Visibility Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleColumnMenuClose}
                PaperProps={{ style: { maxHeight: 48 * 4.5, width: '25ch', borderRadius: 10 } }}
            >
                <Typography variant="overline" display="block" px={2} py={1} fontWeight={700}>Table Columns</Typography>
                {initialHeadCells.map((column) => (
                    <MenuItem
                        key={column.id}
                        onClick={() => handleToggleColumn(column.id)}
                        disabled={column.id !== 'actions' && visibleColumns.length === 1 && visibleColumns.includes(column.id)}
                        sx={{ py: 0.5 }}
                    >
                        <Checkbox checked={visibleColumns.includes(column.id)} size="small" />
                        <ListItemText primary={column.label} primaryTypographyProps={{ variant: 'body2' }} />
                    </MenuItem>
                ))}
            </Menu>

            {/* --- MAIN TABLE AREA --- */}
            <GlassmorphicPaper elevation={6}>
                {fetchError ? (
                    <Alert severity="error" sx={{ m: 4, borderRadius: 1 }}>{fetchError}</Alert>
                ) : users.length === 0 && !loading ? (
                    <Alert severity="info" sx={{ m: 4, borderRadius: 1 }}>No students found in the system. Check API connection or add students.</Alert>
                ) : filteredAndSortedUsers.length === 0 && !loading ? (
                    <Alert severity="warning" sx={{ m: 4, borderRadius: 1 }}>No students match the search criteria: **"{searchTerm}"**</Alert>
                ) : (
                    <>
                        {/* Using CustomScrollbarTableContainer */}
                        <CustomScrollbarTableContainer>
                            <Table stickyHeader size="medium">
                                <TableHead>
                                    <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: theme.palette.primary.main, color: theme.palette.common.white, borderBottom: `2px solid ${theme.palette.primary.dark}` } }}>
                                        {displayedHeadCells.map((headCell) => (
                                            <TableCell
                                                key={headCell.id}
                                                align={headCell.align || 'left'}
                                                sortDirection={orderBy === headCell.id ? order : false}
                                                sx={{
                                                    minWidth: headCell.id === 'name' ? 250 : 150,
                                                    p: 2, // Slightly more padding
                                                }}
                                            >
                                                {headCell.sortable ? (
                                                    <TableSortLabel
                                                        active={orderBy === headCell.id}
                                                        direction={orderBy === headCell.id ? order : 'asc'}
                                                        onClick={() => headCell.id && handleRequestSort(headCell.id)}
                                                        IconComponent={MdSort} // Using a better sort icon
                                                        sx={{ color: theme.palette.common.white, '& .MuiTableSortLabel-icon': { color: `${theme.palette.common.white} !important` } }}
                                                    >
                                                        {headCell.label}
                                                    </TableSortLabel>
                                                ) : (
                                                    headCell.label
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading && users.length === 0 ? (
                                        <TableRow><TableCell colSpan={initialHeadCells.length} align="center"><CircularProgress size={24} sx={{ m: 2 }} /></TableCell></TableRow>
                                    ) : paginatedUsers.map((user) => {
                                        const isUnenrolled = user.course_name === null;
                                        const avatarBg = stringToColor(user.name);
                                        return (
                                            // Using StyledTableRow for better visual feedback
                                            <StyledTableRow key={user.id} isUnenrolled={isUnenrolled}>
                                                {visibleColumns.includes('name') &&
                                                    <TableCell component="th" scope="row">
                                                        <Stack direction="row" alignItems="center" spacing={2}>
                                                            <Tooltip title={user.name}>
                                                                <Avatar sx={{ bgcolor: avatarBg, width: 36, height: 36, fontSize: '0.9rem', boxShadow: theme.shadows[1] }}>
                                                                    {getInitials(user.name)}
                                                                </Avatar>
                                                            </Tooltip>
                                                            <Typography fontWeight={600} color={theme.palette.primary.dark}>{user.name}</Typography>
                                                        </Stack>
                                                    </TableCell>
                                                }
                                                {visibleColumns.includes('email') &&
                                                    <TableCell>
                                                        <Typography fontWeight={600} variant="body2">ID: {user.id}</Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{user.email || 'N/A'}</Typography>
                                                    </TableCell>
                                                }
                                                {visibleColumns.includes('course_name') &&
                                                    <TableCell>
                                                        {isUnenrolled ? (
                                                            <Chip
                                                                label="Unenrolled"
                                                                size="medium" // Slightly larger chip
                                                                color="error"
                                                                variant="outlined"
                                                                icon={<MdPersonOff />}
                                                                sx={{ fontWeight: 600, border: `1px solid ${theme.palette.error.light}` }}
                                                            />
                                                        ) : (
                                                            <Chip
                                                                label={user.course_name}
                                                                size="small"
                                                                color="success"
                                                                variant="outlined"
                                                                icon={<MdSchool size={15} />}
                                                                sx={{ fontWeight: 600, '& .MuiChip-label': { fontSize: '0.75rem' } }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                }
                                                {visibleColumns.includes('actions') &&
                                                    <TableCell align="right">
                                                        <Tooltip title={`Modify course for ${user.name}`}>
                                                            <Button
                                                                variant="contained"
                                                                color="secondary"
                                                                size="small"
                                                                startIcon={<MdSwapHorizontalCircle />} // Improved icon
                                                                onClick={() => handleOpenDialog(user)}
                                                                sx={{ minWidth: 110, borderRadius: 10 }}
                                                            >
                                                                Change
                                                            </Button>
                                                        </Tooltip>
                                                    </TableCell>
                                                }
                                            </StyledTableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </CustomScrollbarTableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={filteredAndSortedUsers.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Rows per page:"
                            sx={{ borderTop: `1px solid ${theme.palette.divider}`, bgcolor: 'rgba(255, 255, 255, 0.9)' }} // Glass effect on pagination
                        />
                    </>
                )}
            </GlassmorphicPaper>

            {/* Course Change Dialog */}
            {selectedUser && (
                <ChangeCourseDialog
                    open={dialogOpen}
                    user={selectedUser}
                    onClose={() => setDialogOpen(false)}
                    onUpdateSuccess={handleUpdateSuccess}
                />
            )}

            {/* Success Snackbar */}
            <Snackbar
                open={updateSuccess}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="success" variant="filled" sx={{ width: '100%', fontWeight: 600 }}>
                    Course successfully updated for **{selectedUser?.name}**!
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CourseControl;